import React, { useEffect, useState } from "react";
import { Button, Form, Spinner, Modal } from "react-bootstrap";
import { X } from "lucide-react";
import {
  backfillAllBoards,
  getBoardDefaults,
  resetBoardDefaults,
  updateBoardDefaults,
} from "./api/boardApi";
import { errorToast, successToast } from "../../redux/actionCreator";
import { getSession } from "../../utils/session";
import "./boardSettings.scss";

// Super-admin page for editing the board template every new board is seeded
// with. Also exposes the bulk backfill action to apply default columns to
// existing boards.
//
// Two editable sections:
//   1) Kinds — the entries shown in the +Add item dropdown across the app.
//      Each has { id, label, color, container }. The Type column auto-fill
//      uses the kind id, so adding a new kind here AND a matching status
//      label in the Type column makes auto-fill work for it.
//   2) Columns — column templates inserted when a new board is created.
//      For status columns, edit the labels[] inline.
const COLUMN_TYPE_OPTIONS = [
  "text", "long_text", "number", "status", "people", "date",
  "dropdown", "checkbox", "rating", "link", "email", "phone", "tags",
];

const SWATCHES = [
  "#c4c4c4", "#9aadbd", "#5559df", "#ef3a3a", "#00c875",
  "#fdab3d", "#ff7575", "#df2f4a", "#bb3354", "#784bd1",
];

export default function BoardSettings() {
  const role = getSession()?.role;
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmBackfill, setConfirmBackfill] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getBoardDefaults();
      setDoc(res.data);
    } catch (e) {
      errorToast(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (role !== "super_admin") {
    return (
      <div className="board-settings-page denied">
        <h2>Board Settings</h2>
        <p>This page is restricted to super-admins.</p>
      </div>
    );
  }

  if (loading) return <div className="board-settings-page"><Spinner animation="border" size="sm" /> Loading…</div>;
  if (!doc) return null;

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateBoardDefaults({ kinds: doc.kinds, columns: doc.columns });
      setDoc(res.data);
      successToast("Defaults saved");
    } catch (e) {
      errorToast(e.message);
    } finally {
      setSaving(false);
    }
  };

  const doReset = async () => {
    setConfirmReset(false);
    try {
      const res = await resetBoardDefaults();
      setDoc(res.data);
      successToast("Restored factory defaults");
    } catch (e) {
      errorToast(e.message);
    }
  };

  const doBackfill = async () => {
    setConfirmBackfill(false);
    setBackfilling(true);
    try {
      const res = await backfillAllBoards();
      successToast(res.message);
    } catch (e) {
      errorToast(e.message);
    } finally {
      setBackfilling(false);
    }
  };

  // ── kinds helpers ─────────────────────────────────────────────
  const addKind = () =>
    setDoc((d) => ({
      ...d,
      kinds: [...d.kinds, { id: `kind${d.kinds.length + 1}`, label: "New kind", color: "#c4c4c4", container: false }],
    }));
  const updateKind = (idx, patch) =>
    setDoc((d) => ({ ...d, kinds: d.kinds.map((k, i) => (i === idx ? { ...k, ...patch } : k)) }));
  const removeKind = (idx) =>
    setDoc((d) => ({ ...d, kinds: d.kinds.filter((_, i) => i !== idx) }));

  // ── columns helpers ───────────────────────────────────────────
  const addColumn = () =>
    setDoc((d) => ({
      ...d,
      columns: [
        ...d.columns,
        { type: "text", title: "New column", order: d.columns.length, width: 160, settings: {}, system_key: "" },
      ],
    }));
  const updateColumn = (idx, patch) =>
    setDoc((d) => ({ ...d, columns: d.columns.map((c, i) => (i === idx ? { ...c, ...patch } : c)) }));
  const removeColumn = (idx) =>
    setDoc((d) => ({ ...d, columns: d.columns.filter((_, i) => i !== idx) }));
  const updateLabel = (colIdx, labelIdx, patch) =>
    setDoc((d) => ({
      ...d,
      columns: d.columns.map((c, i) =>
        i === colIdx
          ? {
              ...c,
              settings: {
                ...(c.settings || {}),
                labels: (c.settings?.labels || []).map((l, j) => (j === labelIdx ? { ...l, ...patch } : l)),
              },
            }
          : c,
      ),
    }));
  const addLabel = (colIdx) =>
    setDoc((d) => ({
      ...d,
      columns: d.columns.map((c, i) =>
        i === colIdx
          ? {
              ...c,
              settings: {
                ...(c.settings || {}),
                labels: [
                  ...(c.settings?.labels || []),
                  {
                    id: `label${(c.settings?.labels || []).length + 1}`,
                    label: "New label",
                    color: "#c4c4c4",
                  },
                ],
              },
            }
          : c,
      ),
    }));
  const removeLabel = (colIdx, labelIdx) =>
    setDoc((d) => ({
      ...d,
      columns: d.columns.map((c, i) =>
        i === colIdx
          ? {
              ...c,
              settings: {
                ...(c.settings || {}),
                labels: (c.settings?.labels || []).filter((_, j) => j !== labelIdx),
              },
            }
          : c,
      ),
    }));

  return (
    <div className="board-settings-page">
      <header className="bs-head">
        <div>
          <h2>Board Defaults</h2>
          <p className="bs-subtitle">
            These templates are used every time a new board is created. Existing boards are not touched
            unless you click <strong>Apply to all boards</strong>.
          </p>
        </div>
        <div className="bs-head-actions">
          <Button
            variant="outline-secondary"
            disabled={backfilling}
            onClick={() => setConfirmBackfill(true)}
          >
            {backfilling ? "Applying…" : "Apply to all boards"}
          </Button>
          <Button variant="outline-secondary" onClick={() => setConfirmReset(true)}>
            Reset to factory
          </Button>
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </header>

      <section className="bs-section">
        <h3>Item kinds</h3>
        <p className="bs-help">
          Each entry is an option in the <code>+ Add item</code> dropdown. <code>id</code> matches the
          Type column label id so the cell auto-fills when an item is created of that kind.
          Toggle <em>container</em> on a kind that should accept sub-items (e.g. Module).
        </p>
        <table className="bs-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Label</th>
              <th>Color</th>
              <th>Container</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {doc.kinds.map((k, i) => (
              <tr key={i}>
                <td>
                  <Form.Control
                    size="sm"
                    value={k.id}
                    onChange={(e) => updateKind(i, { id: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    value={k.label}
                    onChange={(e) => updateKind(i, { label: e.target.value })}
                  />
                </td>
                <td><ColorPicker value={k.color} onChange={(c) => updateKind(i, { color: c })} /></td>
                <td className="bs-center">
                  <Form.Check
                    type="checkbox"
                    checked={!!k.container}
                    onChange={(e) => updateKind(i, { container: e.target.checked })}
                  />
                </td>
                <td><button className="bs-del" onClick={() => removeKind(i)} aria-label="Remove kind"><X size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <Button variant="link" size="sm" onClick={addKind}>+ Add kind</Button>
      </section>

      <section className="bs-section">
        <h3>Default columns</h3>
        <p className="bs-help">
          These columns are added to every newly-created board. Status columns can hold any number of
          colored labels. Set a <code>system_key</code> (free-form string) if you want the column to
          survive title changes — e.g. <code>type</code> keeps the kind→Type auto-fill working.
        </p>
        {doc.columns.map((c, i) => (
          <div key={i} className="bs-col-card">
            <div className="bs-col-head">
              <Form.Control
                size="sm"
                className="bs-col-title"
                value={c.title}
                onChange={(e) => updateColumn(i, { title: e.target.value })}
              />
              <Form.Select
                size="sm"
                className="bs-col-type"
                value={c.type}
                onChange={(e) => updateColumn(i, { type: e.target.value })}
              >
                {COLUMN_TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </Form.Select>
              <Form.Control
                size="sm"
                className="bs-col-systemkey"
                placeholder="system_key (optional)"
                value={c.system_key || ""}
                onChange={(e) => updateColumn(i, { system_key: e.target.value })}
              />
              <Form.Control
                size="sm"
                type="number"
                className="bs-col-width"
                value={c.width || 160}
                onChange={(e) => updateColumn(i, { width: Number(e.target.value) || 160 })}
                title="Width (px)"
              />
              <button className="bs-del" onClick={() => removeColumn(i)} aria-label="Remove column"><X size={12} /></button>
            </div>

            {c.type === "status" && (
              <div className="bs-labels">
                {(c.settings?.labels || []).map((l, j) => (
                  <div key={j} className="bs-label-row" style={{ background: l.color }}>
                    <Form.Control
                      size="sm"
                      className="bs-label-id"
                      value={l.id}
                      placeholder="id"
                      onChange={(e) =>
                        updateLabel(i, j, {
                          id: e.target.value.toLowerCase().replace(/\s+/g, "_"),
                        })
                      }
                    />
                    <Form.Control
                      size="sm"
                      className="bs-label-text"
                      value={l.label}
                      placeholder="label"
                      onChange={(e) => updateLabel(i, j, { label: e.target.value })}
                    />
                    <ColorPicker value={l.color} onChange={(col) => updateLabel(i, j, { color: col })} />
                    <button className="bs-del" onClick={() => removeLabel(i, j)} aria-label="Remove label"><X size={10} /></button>
                  </div>
                ))}
                <Button variant="link" size="sm" onClick={() => addLabel(i)}>+ Add label</Button>
              </div>
            )}
          </div>
        ))}
        <Button variant="link" size="sm" onClick={addColumn}>+ Add column template</Button>
      </section>

      <Modal show={confirmReset} onHide={() => setConfirmReset(false)} centered>
        <Modal.Header closeButton><Modal.Title>Reset to factory defaults?</Modal.Title></Modal.Header>
        <Modal.Body>This discards your customizations and restores the built-in templates.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmReset(false)}>Cancel</Button>
          <Button variant="danger" onClick={doReset}>Reset</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={confirmBackfill} onHide={() => setConfirmBackfill(false)} centered>
        <Modal.Header closeButton><Modal.Title>Apply defaults to every existing board?</Modal.Title></Modal.Header>
        <Modal.Body>
          For every existing board, any default column whose <code>system_key</code> is missing will be
          appended. Existing columns are untouched. This can take a few seconds for large workspaces.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmBackfill(false)}>Cancel</Button>
          <Button variant="primary" onClick={doBackfill}>Apply</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function ColorPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bs-color-picker">
      <button
        type="button"
        className="bs-color-swatch"
        style={{ background: value }}
        onClick={() => setOpen((o) => !o)}
        title={value}
      />
      {open && (
        <>
          <div className="bs-color-backdrop" onClick={() => setOpen(false)} />
          <div className="bs-color-grid">
            {SWATCHES.map((c) => (
              <button
                type="button"
                key={c}
                className="bs-color-swatch"
                style={{ background: c, outline: c === value ? "2px solid #ef3a3a" : "none" }}
                onClick={() => { onChange(c); setOpen(false); }}
              />
            ))}
            <input
              type="color"
              className="bs-color-custom"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              title="Custom color"
            />
          </div>
        </>
      )}
    </div>
  );
}
